import { useState, useEffect, useRef } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import { Button, Icon, Text, theme } from '@socrait/react';

import { Loading } from '~/components/Loading';
import { ErrorBox } from '~/components/ErrorBox';
import { useStudentDataLogQuery, StudentDataLogEvent, type IncludeKey, type Timeframe } from '~/services/studentData';
import { studentDataStore } from '~/store/studentData/studentDataStore';
import { useActiveStudentContext } from '~/hooks/studentData/useActiveStudentContext';

import { DataLogFilterPanel } from './DataLogFilterPanel';

const EVENT_ICON: Record<string, { icon: 'star' | 'hexagon' | 'square'; color: string }> = {
  praise: { icon: 'star', color: theme.colors.lightPurple },
  warning: { icon: 'hexagon', color: theme.colors.yellow },
  absent: { icon: 'square', color: theme.colors.navy },
  tardy: { icon: 'square', color: theme.colors.navy },
  excused: { icon: 'square', color: theme.colors.navy },
};

function formatDateHeading(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00');
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });
}

function groupEventsByDate(events: StudentDataLogEvent[]): { dateKey: string; events: StudentDataLogEvent[] }[] {
  const map: Record<string, StudentDataLogEvent[]> = {};
  for (const event of events) {
    if (event.type === 'present') continue;
    const key = event.timestamp.slice(0, 10);
    if (!map[key]) map[key] = [];
    map[key].push(event);
  }
  return Object.keys(map)
    .sort((a, b) => b.localeCompare(a))
    .map((dateKey) => ({ dateKey, events: map[dateKey]! }));
}

export function DataLogTab() {
  const { studentId, periodId, periodCreatedAt } = useActiveStudentContext();
  // Reads/writes the shared applied state — Data Log has no draft/Go! gating, so toggling a
  // filter is committed immediately. The store's setters keep the Custom Messages draft in sync.
  const timeframe = studentDataStore((s) => s.timeframe);
  const setTimeframe = studentDataStore((s) => s.setTimeframe);
  const include = studentDataStore((s) => s.appliedInclude);
  const setInclude = studentDataStore((s) => s.setAppliedInclude);
  const [page, setPage] = useState(1);
  const [allEvents, setAllEvents] = useState<StudentDataLogEvent[]>([]);
  const [resetCount, setResetCount] = useState(0);

  const includeKeys = (Object.keys(include) as IncludeKey[]).filter((k) => include[k]);

  const { data, status, hasNextPage } = useStudentDataLogQuery({
    studentId,
    periodId,
    periodCreatedAt,
    timeframe,
    include: includeKeys,
    page,
  });

  // Append incoming page data; ref prevents double-appending on re-renders
  const prevDataRef = useRef<typeof data>(undefined);
  useEffect(() => {
    if (status === 'success' && data && data !== prevDataRef.current) {
      prevDataRef.current = data;
      setAllEvents((prev) => (page === 1 ? data.events : [...prev, ...data.events]));
    }
  }, [data, status, page, resetCount]);

  // Reset pagination when student, timeframe, or include changes
  useEffect(() => {
    setPage(1);
    setAllEvents([]);
    prevDataRef.current = undefined;
    setResetCount((n) => n + 1);
  }, [studentId, timeframe, include]);

  const handleIncludeChange = (key: IncludeKey, checked: boolean) => {
    setInclude({ ...include, [key]: checked });
  };

  const handleTimeframeChange = (t: Timeframe) => {
    setTimeframe(t);
  };

  const handleLoadMore = () => {
    setPage((prev) => prev + 1);
  };

  const groups = groupEventsByDate(allEvents);
  const isInitialLoad = status === 'pending' && page === 1;
  const isLoadingMore = status === 'pending' && page > 1;

  return (
    <View style={styles.container}>
      <DataLogFilterPanel
        include={include}
        timeframe={timeframe}
        onIncludeChange={handleIncludeChange}
        onTimeframeChange={handleTimeframeChange}
      />
      <View style={styles.logPanel}>
        {isInitialLoad && <Loading backgroundColor="transparent" />}
        {status === 'error' && !isInitialLoad && (
          <ErrorBox text="Failed to load data log. Please close this window and try again." />
        )}
        {!isInitialLoad && status !== 'error' && (
          <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
            {groups.length === 0 && status === 'success' ? (
              <Text style={styles.emptyText}>No entries found for the selected filters.</Text>
            ) : (
              groups.map(({ dateKey, events: dateEvents }) => {
                const counts = {
                  praises: dateEvents.filter((event) => event.indication === 'praise').length,
                  warnings: dateEvents.filter((event) => event.indication === 'warning').length,
                  absences: dateEvents.filter((event) => event.indication === 'absent').length,
                };

                return (
                  <View key={dateKey} style={styles.dateGroup}>
                    <Text style={styles.dateHeading}>{formatDateHeading(dateKey)}</Text>
                    <View style={styles.dateContent}>
                      <View style={styles.eventBlock}>
                        {dateEvents.map((event, i) => {
                          const config = EVENT_ICON[event.indication];
                          return (
                            <View key={i} style={styles.eventRow}>
                              {config && (
                                <Icon
                                  type={config.icon}
                                  fill={config.color}
                                  width={14}
                                  height={14}
                                  style={styles.eventIcon}
                                  isSolid={true}
                                />
                              )}
                              <Text style={styles.eventContext}>{event.context}</Text>
                            </View>
                          );
                        })}
                      </View>
                      <View style={styles.eventCounts}>
                        {counts.praises > 0 && (
                          <View style={styles.eventCount}>
                            <Icon type="star" fill={theme.colors.lightPurple} width={24} height={24} isSolid={true} />
                            <Text style={styles.eventCountNumber}>
                              {counts.praises}{' '}
                              <Text style={styles.eventCountText}>praise{counts.praises > 1 ? 's' : ''}</Text>
                            </Text>
                          </View>
                        )}
                        {counts.warnings > 0 && (
                          <View style={styles.eventCount}>
                            <Icon type="hexagon" fill={theme.colors.yellow} width={24} height={24} isSolid={true} />
                            <Text style={styles.eventCountNumber}>
                              {counts.warnings}{' '}
                              <Text style={styles.eventCountText}>warning{counts.warnings > 1 ? 's' : ''}</Text>
                            </Text>
                          </View>
                        )}
                        {counts.absences > 0 && (
                          <View style={[styles.eventCount, { borderBottomWidth: 0 }]}>
                            <Icon type="square" fill={theme.colors.navy} width={24} height={24} isSolid={true} />
                            <Text style={styles.eventCountNumber}>
                              {counts.absences}{' '}
                              <Text style={styles.eventCountText}>absence{counts.absences > 1 ? 's' : ''}</Text>
                            </Text>
                          </View>
                        )}
                      </View>
                    </View>
                  </View>
                );
              })
            )}
            {isLoadingMore && <Loading backgroundColor="transparent" />}
            {hasNextPage && !isLoadingMore && (
              <Button
                name="Load More"
                variant="primary"
                onPress={handleLoadMore}
                containerStyle={styles.loadMoreButton}
              />
            )}
          </ScrollView>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
    gap: theme.spacing.xxlarge,
    minWidth: 0,
  },
  logPanel: {
    flex: 1,
    minWidth: 0,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    gap: theme.spacing.xlarge,
    paddingBottom: theme.spacing.xxlarge,
  },
  dateContent: {
    gap: theme.spacing.medium,
    display: 'flex',
    flexDirection: 'row',
  },
  dateGroup: {
    gap: theme.spacing.medium,
  },
  dateHeading: {
    fontSize: theme.fontSizes.small,
    fontWeight: theme.fontWeights.bold,
    color: theme.colors.navy,
    paddingBottom: theme.spacing.small,
  },
  eventBlock: {
    gap: theme.spacing.medium,
    backgroundColor: theme.colors.lightGray,
    borderRadius: theme.borderRadius.large,
    padding: theme.spacing.xlarge,
    flex: 1,
    justifyContent: 'center',
  },
  eventRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.medium,
  },
  eventCounts: {
    gap: theme.spacing.medium,
  },
  eventCount: {
    flexDirection: 'row',
    flexShrink: 0,
    gap: theme.spacing.medium,
    padding: theme.spacing.medium,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray,
  },
  eventCountNumber: {
    display: 'flex',
    flexDirection: 'column',
    fontSize: theme.fontSizes.large,
    lineHeight: theme.fontSizes.large,
  },
  eventCountText: {
    fontSize: theme.fontSizes.xsmall,
    textTransform: 'uppercase',
  },
  eventIcon: {
    flexShrink: 0,
  },
  eventContext: {
    flex: 1,
    fontSize: theme.fontSizes.small,
    lineHeight: theme.lineHeights.small,
    color: theme.colors.navy,
  },
  emptyText: {
    fontSize: theme.fontSizes.small,
    color: theme.colors.darkGray,
    textAlign: 'center',
    paddingTop: theme.spacing.xxlarge,
  },
  loadMoreButton: {
    alignSelf: 'center',
  },
});
